import { useAtomValue, useSetAtom } from 'jotai'
import { mapSessionAtom } from '@/js/editor/state/mapAtoms.ts'
import { useApi } from './useApi.ts'
import type { Poi } from '@/js/types'

export const usePois = () => {
    const map = useAtomValue(mapSessionAtom)
    const setMapSession = useSetAtom(mapSessionAtom)

    const mapId = map.id
    const pois = map.map?.pois ?? []

    const { callEndpoint } = useApi()

    const replaceCanonicalPois = (nextPois: Array<Poi>) => {
        setMapSession((session) => {
            if (!session.map) {
                return session
            }

            return {
                ...session,
                map: {
                    ...session.map,
                    pois: nextPois,
                },
            }
        })
    }

    const appendCanonicalPoi = (newPoi: Poi) => {
        let nextPois: Array<Poi> = []

        setMapSession((session) => {
            if (!session.map) {
                return session
            }

            nextPois = [...session.map.pois.filter((currentPoi) => String(currentPoi.id) !== String(newPoi.id)), newPoi]

            return {
                ...session,
                map: {
                    ...session.map,
                    pois: nextPois,
                },
            }
        })
    }

    const replaceCanonicalPoi = (updatedPoi: Poi) => {
        let nextPois: Array<Poi> = []

        setMapSession((session) => {
            if (!session.map) {
                return session
            }

            nextPois = session.map.pois.map((currentPoi) =>
                String(currentPoi.id) === String(updatedPoi.id) ? updatedPoi : currentPoi
            )

            return {
                ...session,
                map: {
                    ...session.map,
                    pois: nextPois,
                },
            }
        })
    }

    const fetchPois = async () => {
        if (!mapId) {
            return []
        }

        const response = await callEndpoint(`/api/maps/${mapId}/pois`)

        if (!response.ok) {
            throw new Error(`Unable to load POIs (${response.status})`)
        }

        const data = (await response.json()) as Array<Poi>

        replaceCanonicalPois(data)

        return data
    }

    const createPoi = async (poi: Partial<Poi>) => {
        if (!mapId) {
            throw new Error('Cannot create a POI without a map')
        }

        const response = await callEndpoint('/api/pois', {
            method: 'POST',
            body: JSON.stringify({
                ...poi,
                map_id: mapId,
            }),
        })

        if (!response.ok) {
            throw new Error(`Unable to create POI (${response.status})`)
        }

        const newPoi = (await response.json()) as Poi

        appendCanonicalPoi(newPoi)

        return newPoi
    }

    const updatePoi = async (poi: Poi) => {
        const response = await callEndpoint(`/api/pois/${poi.id}`, {
            method: 'PUT',
            body: JSON.stringify(poi),
        })

        if (!response.ok) {
            throw new Error(`Unable to update POI (${response.status})`)
        }

        const savedPoi = (await response.json()) as Poi

        replaceCanonicalPoi(savedPoi)

        return savedPoi
    }

    const deletePoi = async (poi: Poi) => {
        const response = await callEndpoint(`/api/pois/${poi.id}`, {
            method: 'DELETE',
        })

        if (!response.ok) {
            throw new Error(`Unable to delete POI (${response.status})`)
        }

        replaceCanonicalPois(pois.filter((currentPoi) => String(currentPoi.id) !== String(poi.id)))

        return response.status
    }

    const saveAllPois = async () => {
        for (const poi of pois) {
            await updatePoi(poi)
        }
    }

    return {
        pois,
        fetchPois,
        saveAllPois,
        createPoi,
        updatePoi,
        deletePoi,
    }
}
